(() => {
  if (window.ScrptdPredictiveSearchReady) return;
  window.ScrptdPredictiveSearchReady = true;

  const inputSelector = [
    '.scrptd-search-overlay input[type="search"]',
    '.scrptd-menu-search input[type="search"]',
    '#searchresults input[type="search"]',
    'predictive-search input[type="search"]'
  ].join(',');
  const state = new WeakMap();
  let resultId = 0;

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const config = () => {
    const overlay = document.querySelector('[data-scrptd-search-overlay]');
    return {
      limit: Number(overlay?.dataset.suggestionLimit || 6),
      heading: overlay?.dataset.suggestionHeading || 'PRODUKTVORSCHLÄGE',
      empty: overlay?.dataset.suggestionEmpty || 'KEINE PRODUKTE GEFUNDEN',
      all: overlay?.dataset.suggestionAll || 'ALLE ERGEBNISSE ANZEIGEN'
    };
  };

  const formatPrice = (value) => {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return '';
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'de-DE';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2).replace('.', ',')} ${currency}`;
    }
  };

  const getImage = (product) => {
    const image = product.featured_image;
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  };

  const createHost = (input) => {
    const form = input.closest('form');
    if (!form) return null;
    const menuSearch = form.closest('.scrptd-menu-search');
    let host = menuSearch
      ? menuSearch.querySelector(':scope > .scrptd-menu-search__panel > .scrptd-suggestions')
      : form.querySelector(':scope > .scrptd-suggestions, :scope > #predictive-search');
    if (!host) {
      host = document.createElement('div');
      if (menuSearch) form.insertAdjacentElement('afterend', host);
      else form.appendChild(host);
    }
    host.classList.add('scrptd-suggestions');
    host.id = `scrptd-suggestions-${++resultId}`;
    host.hidden = true;
    host.setAttribute('role', 'listbox');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', host.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');
    return host;
  };

  const close = (input) => {
    const current = state.get(input);
    if (!current) return;
    current.host.hidden = true;
    current.host.classList.remove('is-open');
    input.setAttribute('aria-expanded', 'false');
    current.active = -1;
  };

  const open = (input) => {
    const current = state.get(input);
    if (!current) return;
    current.host.hidden = false;
    current.host.classList.add('is-open');
    input.setAttribute('aria-expanded', 'true');
  };

  const render = (input, products, query) => {
    const current = state.get(input);
    if (!current) return;
    const labels = config();
    current.active = -1;

    if (!products.length) {
      current.host.innerHTML = `<p class="scrptd-suggestions__empty">${escapeHtml(labels.empty)}</p>`;
      open(input);
      return;
    }

    const items = products.map((product, index) => {
      const image = getImage(product);
      const price = formatPrice(product.price);
      return `<li role="option" id="${current.host.id}-option-${index}">
        <a class="scrptd-suggestions__item" href="${escapeHtml(product.url)}" tabindex="-1">
          <span class="scrptd-suggestions__image">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}</span>
          <span class="scrptd-suggestions__copy">
            <strong>${escapeHtml(product.title)}</strong>
            ${price ? `<small>${escapeHtml(price)}</small>` : ''}
          </span>
        </a>
      </li>`;
    }).join('');

    const root = window.Shopify?.routes?.root || '/';
    current.host.innerHTML = `
      <p class="scrptd-suggestions__heading">${escapeHtml(labels.heading)}</p>
      <ul class="scrptd-suggestions__list">${items}</ul>
      <a class="scrptd-suggestions__all" href="${root}search?q=${encodeURIComponent(query)}&type=product">${escapeHtml(labels.all)}</a>`;
    open(input);
  };

  const request = async (input) => {
    const current = state.get(input);
    const query = input.value.trim();
    if (!current || query.length < 2) {
      close(input);
      return;
    }

    current.controller?.abort();
    current.controller = new AbortController();
    const root = window.Shopify?.routes?.root || '/';
    const labels = config();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(labels.limit),
      'resources[options][unavailable_products]': 'last'
    });

    current.host.innerHTML = '<span class="scrptd-suggestions__loading" aria-label="Suche läuft"></span>';
    open(input);
    try {
      const response = await fetch(`${root}search/suggest.json?${params}`, {
        signal: current.controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      if (input.value.trim() !== query) return;
      render(input, data?.resources?.results?.products || [], query);
    } catch (error) {
      if (error.name !== 'AbortError') close(input);
    }
  };

  const move = (input, direction) => {
    const current = state.get(input);
    if (!current || current.host.hidden) return;
    const links = Array.from(current.host.querySelectorAll('.scrptd-suggestions__item'));
    if (!links.length) return;
    current.active = (current.active + direction + links.length) % links.length;
    links.forEach((link, index) => link.classList.toggle('is-active', index === current.active));
    input.setAttribute('aria-activedescendant', `${current.host.id}-option-${current.active}`);
  };

  const initInput = (input) => {
    if (input.dataset.scrptdSuggestionsReady === 'true') return;
    const host = createHost(input);
    if (!host) return;
    input.dataset.scrptdSuggestionsReady = 'true';
    state.set(input, { host, timer: 0, controller: null, active: -1 });

    input.addEventListener('input', () => {
      const current = state.get(input);
      window.clearTimeout(current.timer);
      current.timer = window.setTimeout(() => request(input), 180);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && host.children.length) open(input);
    });
    input.addEventListener('keydown', (event) => {
      const current = state.get(input);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        move(input, event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Enter' && current.active >= 0) {
        const link = host.querySelectorAll('.scrptd-suggestions__item')[current.active];
        if (link) {
          event.preventDefault();
          link.click();
        }
      } else if (event.key === 'Escape') {
        close(input);
      }
    });
  };

  const init = (scope = document) => scope.querySelectorAll(inputSelector).forEach(initInput);
  document.addEventListener('click', (event) => {
    document.querySelectorAll(inputSelector).forEach((input) => {
      if (!input.closest('form')?.contains(event.target)) close(input);
    });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
